// Package imports:
import React, { useEffect, useState } from 'react';
// Component imports:
import CalculatedColoredNumber from '../../ui-elements/CalculatedColoredNumber/CalculatedColoredNumber';
import Link from '../../ui-elements/Link/Link';
import TandemTable from '../../ui-elements/Table/TandemTable';
import Alert from '../../ui-elements/Alert/Alert';
import Loading from '../../ui-elements/Loading/Loading';
import DisplayBox from '../../ui-elements/DisplayBox/DisplayBox';
// Service imports:
import { formatNumber, getTableDate, getLastDayOfLastMonth } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdFundPrice } from '../../types/FundsTypes';

interface IOwnProps {
    basecurrency?: string
}
type Props = IDefaultProps & IOwnProps & React.HTMLAttributes<HTMLDivElement>;

const SpecialFundsTable: React.FC<Props> = ({
    accessToken,
    refreshRateMs,
    ...props
}) => {

    const [ data ] = useApiLmdData<IApiLmdFundPrice[]>(
        `/v1/market_data/v1/specialfunds/keldan/by_date?date=${getLastDayOfLastMonth()}`,
        accessToken,
        refreshRateMs
    );
    const [dataFrjalsi, setDataFrjalsi ] = useState<IApiLmdFundPrice[]>([]);
    const [dataIsl, setDataIsl ] = useState<IApiLmdFundPrice[]>([]);
    const [dataLif, setDataLif ] = useState<IApiLmdFundPrice[]>([]);

    useEffect(() => {
        if (data.data !== null)
            handleData(data.data);
    }, [data]);

    const handleData = (body: IApiLmdFundPrice[]) => {
        setDataFrjalsi(body.filter(x => x.Symbol?.startsWith('frjalsi')))
        setDataIsl(body.filter(x => x.Symbol?.startsWith('isl')))
        setDataLif(body.filter(x => x.Symbol?.startsWith('lif')))
    }

    const displayData = () => {
        if (data instanceof Error) {
            return <Alert type='error' headText={data.message} />
        }
        if (data === null) {
            return <Loading />
        }
        return <TandemTable
            data={[
                dataIsl,
                dataFrjalsi,
                dataLif
            ]}
            columns={[[
                {
                    title: 'Framtíðarauður Íslandsbanka',
                    renderCell: ( {Name, Symbol: symbol} ) => (
                        <Link url={`/Markadir/Sjodir/${symbol ?? ''}`} linkSize='15'>
                            {Name}
                        </Link>
                    ),
                    textAlign: 'left'
                }, {
                    title: 'Gengi',
                    renderCell: ( {LastPrice} ) => (formatNumber(LastPrice, '-', 2)),
                }, {
                    title: 'Dagsetning',
                    renderCell: ( {ValueDate } ) => (ValueDate && getTableDate(ValueDate.toString(), 'DD/MM/YYYY', '.')),
                    
                }, {
                    title: '1 Ár',
                    renderCell: ( {Change12m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change12m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '2 Ár',
                    renderCell: ( {Change24m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change24m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '3 Ár',
                    renderCell: ( {Change36m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change36m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '4 Ár',
                    renderCell: ( {Change48m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change48m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '5 Ár',
                    renderCell: ( {Change60m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change60m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }
            ], [
                {
                    title: 'Frjálsi lífeyrissjóðurinn',
                    renderCell: ( {Name, Symbol: symbol} ) => (
                        <Link url={`/Markadir/Sjodir/${symbol ?? ''}`} linkSize='15'>{Name}</Link>
                    ),
                    textAlign: 'left'
                }, {
                    title: '',
                    renderCell: ( {LastPrice} ) => (formatNumber(LastPrice, '-', 2)),
                }, {
                    title: '',
                    renderCell: ( {ValueDate } ) => (ValueDate && getTableDate(ValueDate.toString(), 'DD/MM/YYYY', '.')),
                    
                }, {
                    title: '',
                    renderCell: ( {Change12m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change12m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change24m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change24m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change36m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change36m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change48m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change48m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change60m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change60m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }
            ], [
                {
                    title: 'Íslenski lífeyrissjóðurinn',
                    renderCell: ( {Name, Symbol: symbol} ) => (
                        <Link url={`/Markadir/Sjodir/${symbol ?? ''}`} linkSize='15'>{Name}</Link>
                    ),
                    textAlign: 'left'
                }, {
                    title: '',
                    renderCell: ( {LastPrice} ) => (formatNumber(LastPrice, '-', 2)),
                }, {
                    title: '',
                    renderCell: ( {ValueDate } ) => (ValueDate && getTableDate(ValueDate.toString(), 'DD/MM/YYYY', '.')),
                    
                }, {
                    title: '',
                    renderCell: ( {Change12m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change12m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change24m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change24m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change36m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change36m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change48m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change48m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }, {
                    title: '',
                    renderCell: ( {Change60m} ) => (
                        <CalculatedColoredNumber
                            currentValue={Change60m}
                            formatting='percent'
                            hasArrow
                            hasColor
                        />
                    ),
                }
            ]]}
            tableSize="lg"
        />
    }

    return (
        <DisplayBox 
            className='KCL_special-funds-table'
            title="Séreignarsjóðir"
            {...props}
        >
            {displayData()}
        </DisplayBox>
    );
}

export default SpecialFundsTable;